import apiClient from './apiClient';
import { TalkRoom, TalkRoomListResponse, TalkRoomSummaryResponse, TalkSearchParams } from '../types/talk';

// 外部データベースのAPIエンドポイント
const BASE_URL = '/external/talk_rooms_with_sessions';

// レスポンスを処理する関数
const processResponse = (data: any): TalkRoomListResponse => {
  // レスポンスがページネーション形式の場合
  if (data && ('results' in data)) {
    return data;
  } 
  // 配列の場合（ページネーションなし）
  else if (Array.isArray(data)) {
    const rooms = data as TalkRoom[];
    return {
      count: rooms.length,
      next: null,
      previous: null,
      results: rooms
    };
  }
  // その他の形式
  else {
    console.warn('不明なレスポンス形式:', data);
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  }
};

// 対話一覧を取得
export const getTalkRooms = async (params: TalkSearchParams = {}): Promise<TalkRoomListResponse> => {
  // パラメータを加工
  const queryParams = { ...params };
  try {
    // talk_statusまたはpriceが配列の場合、URLクエリパラメータを正しく処理
    if ((queryParams.talk_status && Array.isArray(queryParams.talk_status) && queryParams.talk_status.length > 0) ||
      (queryParams.price && Array.isArray(queryParams.price) && queryParams.price.length > 0)) {
      // 配列パラメータを保存
      const statusTypes = queryParams.talk_status;
      const prices = queryParams.price;
      delete queryParams.talk_status;
      delete queryParams.price;
      
      // URLSearchParamsを使用してパラメータを生成
      const urlParams = new URLSearchParams();
      
      // 配列以外のパラメータをコピー
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
          urlParams.append(key, String(value));
        }
      });
      
      // 各ステータスタイプを個別のパラメータとして追加
      if (Array.isArray(statusTypes)) {
        statusTypes.forEach(status => {
          urlParams.append('talk_status', status);
        });
      }
      
      // 各価格を個別のパラメータとして追加
      if (Array.isArray(prices)) {
        prices.forEach(price => {
          urlParams.append('price', String(price));
        });
      }
      
      const fullUrl = `${BASE_URL}?${urlParams.toString()}`;
      
      try {
        // URLSearchParamsを使用してGETリクエストを送信
        const response = await apiClient.get<TalkRoomListResponse>(fullUrl);
        return processResponse(response.data);
      } catch (error) {
        // エラーがページネーションに関連する場合は、ページをリセットして再試行
        // ページネーションを1ページ目にリセット
        urlParams.set('page', '1');
        const retryUrl = `${BASE_URL}?${urlParams.toString()}`;
        const retryResponse = await apiClient.get<TalkRoomListResponse>(retryUrl);
        return processResponse(retryResponse.data);
      }
    } else {
      // 通常のパラメータ処理
      try {
        const response = await apiClient.get<TalkRoomListResponse>(BASE_URL, { params: queryParams });
        return processResponse(response.data);
      } catch (error) {
        // エラーがページネーションに関連する場合は、ページをリセットして再試行
        // パラメータを複製して変更
        const retryParams = { ...queryParams, page: 1 };
        const retryResponse = await apiClient.get<TalkRoomListResponse>(BASE_URL, { params: retryParams });
        return processResponse(retryResponse.data);
      }
    }
  } catch (error) {
    console.error('対話一覧取得エラー:', error);
    throw error;
  }
};

// 対話ルーム詳細を取得
export const getTalkRoom = async (id: number): Promise<TalkRoom> => {
  const response = await apiClient.get<TalkRoom>(`${BASE_URL}/${id}/`);
  return response.data;
};

// 看護師と患者IDから対話ルームを検索（詳細情報を含む）
export const getTalkRoomsByNurseAndPatient = async (nurseId: number, patientId: number): Promise<TalkRoomSummaryResponse> => {
  const params = {
    parent_id: nurseId,
    child_id: patientId
  };

  try {
    // 対話サマリー専用のエンドポイントを使用
    const response = await apiClient.get<TalkRoomSummaryResponse>('/external/talk_room_summary', { params });
    return response.data;
  } catch (error) {
    console.error('看護師・患者IDによる対話ルームサマリー検索エラー:', error);
    throw error;
  }
};

// 価格オプションを取得
export const getPriceOptions = async (): Promise<{ value: number; label: string }[]> => {
  try {
    const response = await apiClient.get<{ prices: { value: number; label: string }[] }>(
      `${BASE_URL}/price_options/`
    );
    return response.data.prices;
  } catch (error) {
    console.error('価格オプション取得エラー:', error);
    return [];
  }
};

const talkRoomsApi = {
  getTalkRooms,
  getTalkRoom,
  getTalkRoomsByNurseAndPatient,
  getPriceOptions
};

export default talkRoomsApi;
